import React, { useCallback } from 'react';
import DocumentTitle from 'react-document-title';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { bindActionCreators } from 'redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import messages from './messages';
import { useManageFloorplans } from './hooks-main';
import HudLimitsModal from './HudLimitsModal';
import UnitsByFloorPlanModal from './UnitsByFloorPlanModal';
import TableLayout from '../../components/TableLayout';
import HudLimitsBanner from './HudLimitsBanner';
import { promptToaster } from '../App/actions';
import useBaseTableProps from '../../hooks/useBaseTableProps';
import RDLimitsBanner from './RD/RDLimitsBanner.jsx';
import RDLimitsModal from './RD/RDLimitsModal.jsx';

const NAME = 'manage-floorplans';
const ManageFloorplans = ({
  intl,
  selectedProperty,
  specialsForm,
  actions,
}) => {
  const { section236: section236Flag = false, rdGrossRentCerts } = useFlags();

  const [hudLimitsModalConfig, setHudLimitsModalConfig] = React.useState({
    show: false,
  });

  const [rdLimitsModalConfig, setRDLimitsModalConfig] = React.useState({
    show: false,
  });

  const [unitsByFloorPlanModalConfig, setUnitsByFloorPlanModalConfig] =
    React.useState({
      show: false,
      floorPlanId: '',
      floorInternalName: '',
      numUnits: 0,
    });

  const openUnitsByFloorplanModal = useCallback(
    ({ floorPlanId, floorInternalName, numUnits }) => {
      setUnitsByFloorPlanModalConfig({
        show: true,
        floorPlanId,
        floorInternalName,
        numUnits,
      });
    },
    [],
  );

  const openHudLimitsEdit = useCallback(() => {
    setHudLimitsModalConfig({ show: true });
  }, []);

  const baseTableProps = useBaseTableProps();
  const openRDLimitsEdit = useCallback(() => {
    setRDLimitsModalConfig({ show: true });
  }, []);

  const {
    allColumnsHidden,
    count,
    totalCount,
    floorPlans,
    columnOptions,
    isLoading,
    isAffordableMixedProperty,
    filterState,
    filterTypeState,
    filteredHeaders,
    footerRow,
    order,
    orderBy,
    name,
    hasHUD,
    hasRD,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleColumnChange,
    selectedColumns,
    refetchFloorPlans,
    searchState,
    onCSVButtonClick,
    updatedHudGrcLogic,
  } = useManageFloorplans({
    NAME,
    intl,
    selectedProperty,
    openUnitsByFloorplanModal,
    flags: {
      section236Flag,
    },
  });

  return (
    <DocumentTitle
      data-testid={'manage-floor-plans'}
      title={'Fortress- Manage Floor Plans'}
    >
      <>
        <TableLayout name={name} childrenElementsHeight={50 + 8}>
          {(tableHeight) => (
            <>
              <TableHeader
                title={<FormattedMessage {...messages.header} />}
                count={count}
                totalCount={totalCount}
                actions={[
                  <RDLimitsBanner
                    hasRD={hasRD && rdGrossRentCerts}
                    openRDLimitsEdit={openRDLimitsEdit}
                    intl={intl}
                  />,
                ]}
              />
              <HudLimitsBanner
                isLoading={isLoading}
                hasHud={hasHUD && updatedHudGrcLogic === false}
                openHudLimitsEdit={openHudLimitsEdit}
                intl={intl}
              ></HudLimitsBanner>

              <Table
                {...baseTableProps}
                allColumnsHidden={allColumnsHidden}
                columnOptions={columnOptions}
                filterState={filterState}
                filterTypeState={filterTypeState}
                footerRow={footerRow}
                handleColumnChange={handleColumnChange}
                handleFilterChange={handleFilterChange}
                handleFilterTypeChange={handleFilterTypeChange}
                handleSearchSubmit={handleSearchSubmit}
                handleSortChange={handleSortChange}
                headers={filteredHeaders}
                height={tableHeight}
                isLoading={isLoading}
                name={name}
                onCSVButtonClick={onCSVButtonClick}
                order={order}
                orderBy={orderBy}
                rows={floorPlans}
                count={count}
                searchState={searchState}
                selectedColumns={selectedColumns}
                totalCount={totalCount}
              />
            </>
          )}
        </TableLayout>
        <HudLimitsModal
          intl={intl}
          isAffordableMixedProperty={isAffordableMixedProperty}
          show={hudLimitsModalConfig.show}
          onClose={() => setHudLimitsModalConfig({ show: false })}
          propertyId={selectedProperty?.id}
          organizationId={selectedProperty?.organizationId}
          promptToaster={actions.promptToaster}
        />
        <RDLimitsModal
          open={rdLimitsModalConfig.show}
          onClose={() => setRDLimitsModalConfig({ show: false })}
          propertyId={selectedProperty?.id}
          organizationId={selectedProperty?.organizationId}
          intl={intl}
          selectedProperty={selectedProperty}
          refetchFloorPlans={refetchFloorPlans}
        />
        <UnitsByFloorPlanModal
          intl={intl}
          propertyId={selectedProperty?.id}
          organizationId={selectedProperty?.organizationId}
          floorPlanId={unitsByFloorPlanModalConfig?.floorPlanId}
          floorInternalName={unitsByFloorPlanModalConfig?.floorInternalName}
          numUnits={unitsByFloorPlanModalConfig?.numUnits}
          show={unitsByFloorPlanModalConfig?.show}
          onClose={() =>
            setUnitsByFloorPlanModalConfig({
              ...unitsByFloorPlanModalConfig,
              show: false,
            })
          }
        />
      </>
    </DocumentTitle>
  );
};

export const mapStateToProps = ({ app }: Object): Object => {
  return {
    organizationId: app.currentUser?.user?.organizationId || '',
    selectedProperty: app.selectedProperty,
  };
};
export const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  actions: bindActionCreators(
    {
      promptToaster,
    },
    dispatch,
  ),
});
export default connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
)(injectIntl(ManageFloorplans));
